// API helpers for the IPD Discharge Process workflow (Epic 3). Tenant
// headers are sent explicitly on every call — see bedTransferRequestApi.js.
//
// This module is the first consumer of the discharge-process/ endpoint
// family; later tasks (fetch, cancel, summary-status) add their own
// exported functions here rather than creating separate files.

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

// deathDetails is only accepted by the backend when outcome === "deceased"
// (it rejects the fields outright otherwise), so callers must gate on the
// outcome radio before passing it — see DischargeModal.js.
export async function createDischargeProcess({
  admissionId,
  outcome,
  adviceDatetime,
  remarks,
  deathDetails,
}) {
  const body = {
    admission_id: admissionId,
  };
  if (outcome) body.outcome = outcome;
  if (adviceDatetime) body.advice_datetime = adviceDatetime;
  if (remarks) body.remarks = remarks;
  if (outcome === "deceased" && deathDetails) {
    Object.assign(body, deathFieldsBody(deathDetails));
    // Time of death is its own field, not advice_datetime.
    if (deathDetails.timeOfDeath) body.death_datetime = deathDetails.timeOfDeath;
  }
  const res = await axios.post(`${BASE}ipd/discharge-process/`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return res?.data;
}

// The statutory manner-of-death set, verbatim from MCCD Form 4 (prescribed under
// the Registration of Births and Deaths Act).
//
// This replaces {natural, accidental, mlc}, which was wrong twice: suicide,
// homicide and pending-investigation were unrepresentable, and "mlc" is not a
// manner of death at all — a medico-legal case is a case TYPE. Medico-legal
// handling is out of scope entirely, so "mlc" is simply gone.
//
// "Pending investigation" is not an edge case: Form 4's own instructions say that
// where the cause of death is known but it is not known whether it was accident,
// suicide or homicide, the cause must still be filled in and the manner shown as
// pending.
export const MANNER_OF_DEATH_OPTIONS = [
  { value: "natural", label: "Natural" },
  { value: "accident", label: "Accident" },
  { value: "suicide", label: "Suicide" },
  { value: "homicide", label: "Homicide" },
  { value: "pending_investigation", label: "Pending investigation" },
];

export const MANNER_OF_DEATH_LABELS = MANNER_OF_DEATH_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {}
);

// Only keys the caller actually set are sent — the backend treats an absent key
// as "leave alone" and a present-but-empty one as "clear", so omitting them is
// what makes a partial edit (e.g. filling in just the certificate number weeks
// later) safe. certifying_doctor_name is NOT sent: the backend resolves it from
// DoctorRecord itself.
function deathFieldsBody({
  causeOfDeath,
  mannerOfDeath,
  certifyingDoctorId,
  deathCertificateNumber,
}) {
  const body = {};
  if (causeOfDeath !== undefined) body.cause_of_death = causeOfDeath;
  if (mannerOfDeath !== undefined) body.manner_of_death = mannerOfDeath;
  if (certifyingDoctorId !== undefined) body.certifying_doctor_id = certifyingDoctorId;
  if (deathCertificateNumber !== undefined) {
    body.death_certificate_number = deathCertificateNumber;
  }
  return body;
}

// Record or correct the death-register fields on a deceased discharge process.
//
// timeOfDeath maps to `death_datetime` — its own field, NOT advice_datetime
// (which means "tentative discharge advice", a planning value). This endpoint
// stays open after save and after the body has left, because a corrected time of
// death and a certificate number routinely arrive days later. The backend rejects
// any value at or after the physical-discharge timestamp.
export async function updateDeathDetails(id, { timeOfDeath, ...deathFields }) {
  const body = deathFieldsBody(deathFields);
  if (timeOfDeath) body.death_datetime = timeOfDeath;
  const res = await axios.put(
    `${BASE}ipd/discharge-process/${id}/death-details/`,
    body,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// Record the MCCD (Form 4) — the doctor's structured certification of the cause
// of death. `part1` is the ordered causal sequence [{condition, interval}] (line
// a/b/c assigned server-side), `part2` the contributing conditions. The backend
// validates it (contiguous a->b->c, interval per line, no mode of dying as the
// underlying cause), derives cause_of_death, and issues an mccd_number. On a
// non-fatal issue (mode of dying as an immediate cause, multiple conditions on a
// line) the save succeeds and the reasons come back in `warnings`.
export async function updateMccd(id, {
  part1,
  part2,
  mannerOfDeath,
  certifyingDoctorId,
  deathCertificateNumber,
  injuryDescription,
  pregnancyAssociated,
  deliveryOccurred,
  timeOfDeath,
}) {
  const body = { mccd_part1: part1 || [], mccd_part2: part2 || [] };
  if (mannerOfDeath !== undefined) body.manner_of_death = mannerOfDeath;
  if (certifyingDoctorId !== undefined) body.certifying_doctor_id = certifyingDoctorId;
  if (deathCertificateNumber !== undefined) body.death_certificate_number = deathCertificateNumber;
  if (injuryDescription !== undefined) body.mccd_injury_description = injuryDescription;
  if (pregnancyAssociated !== undefined) body.mccd_pregnancy_associated = pregnancyAssociated;
  if (deliveryOccurred !== undefined) body.mccd_delivery_occurred = deliveryOccurred;
  if (timeOfDeath) body.death_datetime = timeOfDeath;
  const res = await axios.put(
    `${BASE}ipd/discharge-process/${id}/mccd/`,
    body,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// GET DischargeProcess rows across admissions (Task C21/C16), optionally
// filtered by stage and/or outcome. Rows are bare — no patient/ward fields —
// callers that need those must join against the admissions list themselves
// (see IPDBillingDischarge.js for the billing-queue join).
export async function listDischargeProcesses({ stage, outcome } = {}) {
  const params = new URLSearchParams();
  if (stage) params.set("stage", stage);
  if (outcome) params.set("outcome", outcome);
  const qs = params.toString();
  const res = await axios.get(
    `${BASE}ipd/discharge-process/${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
  return res?.data?.data || [];
}

// GET the active (non-cancelled) DischargeProcess for an admission, if one
// exists. Response is always {status: "Success", ...} — on a hit, data has
// the DischargeProcess fields; on a miss, the "data" key is entirely absent
// (not data: null), so callers must check res?.data?.data truthiness rather
// than an explicit null check.
export async function fetchDischargeProcess(admissionId) {
  const res = await axios.get(
    `${BASE}ipd/discharge-process/${admissionId}/`,
    { headers: authHeaders() }
  );
  return res?.data;
}

// Advance a DischargeProcess exactly one step along the fixed pipeline
// (ward -> billing -> nursing_discharge -> physical_discharge).
//
// deathDatetime is only used for the deceased-outcome re-correction case on the
// terminal nursing_discharge -> physical_discharge transition (the doctor may be
// correcting the time of death as the body is released); omit it for a normal
// advance. Note the backend validates the STORED time of death on that transition
// whether or not it is re-sent here.
//
// noDueOverrideReason applies only to the billing -> nursing_discharge
// transition: the backend REJECTS that advance when the patient has an
// outstanding balance unless a reason is supplied, because clearing billing is
// what issues the No-Due Certificate. The reason is persisted on the row — it is
// the audit trail for why a patient who owed money was cleared anyway.
//
// deathRecordOverrideReason applies only to the terminal transition on a DECEASED
// patient, where physical discharge is release of the body. The backend refuses to
// release a body on an incomplete death record (time of death, cause, manner,
// certifying doctor) unless a reason is supplied — also persisted.
export async function advanceDischargeProcess(
  id,
  { deathDatetime, noDueOverrideReason, deathRecordOverrideReason } = {}
) {
  const body = {};
  if (deathDatetime) body.death_datetime = deathDatetime;
  if (noDueOverrideReason) body.no_due_override_reason = noDueOverrideReason;
  if (deathRecordOverrideReason) {
    body.death_record_override_reason = deathRecordOverrideReason;
  }
  const res = await axios.put(`${BASE}ipd/discharge-process/${id}/advance/`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return res?.data;
}

// Cancel a DischargeProcess before it reaches Physical Discharge. Backend is
// admitting-doctor-only; remarks is required (backend rejects missing/empty).
export async function cancelDischargeProcess(id, remarks) {
  const res = await axios.put(
    `${BASE}ipd/discharge-process/${id}/cancel/`,
    { remarks },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// Move summary_status forward along draft -> pending_approval ->
// approved/rejected (Task C6). Only "approved" is admitting-doctor-
// restricted server-side; pending_approval/rejected have no actor
// restriction beyond nurse_auth().
export async function updateSummaryStatus(id, summaryStatus) {
  const res = await axios.put(
    `${BASE}ipd/discharge-process/${id}/summary-status/`,
    { summary_status: summaryStatus },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// Tick/untick the nurse's Nursing-Discharge-stage checklist. `checklist` is a
// partial {item_key: boolean} map — keys omitted keep their stored value. The
// item DEFINITIONS come back on every discharge-process row as `checklist_items`
// (the backend owns the list), so callers render from that rather than keeping
// their own copy. Backend rejects this outside the Nursing Discharge stage.
export async function updateDischargeChecklist(id, checklist) {
  const res = await axios.put(
    `${BASE}ipd/discharge-process/${id}/checklist/`,
    { checklist },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// Shared stage-label text so every DischargeProcess status display (nurse
// task list, patient detail chip, etc.) reads identically.
//
// admission_office is retired from the pipeline but kept here as a label: a
// tenant that hasn't yet run collapse-admission-office-stage.sql can still
// have rows parked at that stage, and they should read as "Admission Office"
// rather than a raw key until the rescue UPDATE moves them to Billing.
export const STAGE_LABELS = {
  ward: "Ward",
  admission_office: "Admission Office",
  billing: "Billing",
  nursing_discharge: "Nursing Discharge",
  physical_discharge: "Physical Discharge",
  cancelled: "Cancelled",
};
